"use client";

import { useEffect, useRef } from "react";
import { useAuth } from "@clerk/nextjs";

import { recordLandingAttribution } from "@/hooks/onboarding/onboardingFunnelApi";
import { useIsMobile } from "@/hooks/useIsMobile";
import { ensureLandingCookie } from "@/lib/analytics/landingCookie";

/** Marks the browser as already synced, so this is one POST per member, not one per load. */
const SYNCED_STORAGE_KEY = "sukha_landing_synced";

/**
 * localStorage access that cannot take the page down with it.
 *
 * Safari in private mode throws on `setItem`, and some privacy settings make
 * the whole property throw on access. This component mounts in the root
 * layout, so an uncaught throw here would break every page — for a flag whose
 * only job is to save a redundant POST the server already ignores.
 */
function readSyncedFlag(): boolean {
  try {
    return window.localStorage.getItem(SYNCED_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function writeSyncedFlag(): void {
  try {
    window.localStorage.setItem(SYNCED_STORAGE_KEY, "true");
  } catch {
    // Storage unavailable. The server is idempotent, so the cost is one
    // repeat POST per load, not a duplicated row.
  }
}

/**
 * Captures where a member arrived from, and hands it to the server once.
 *
 * Two separate jobs, deliberately in one place so the ordering is obvious:
 * the cookie is written on every visitor including anonymous ones, and it is
 * copied to the database only once the visitor has a member id to attach it
 * to. The server ignores repeat calls, so the storage flag here is a courtesy
 * to the network rather than the thing that guarantees write-once.
 */
export function LandingAttributionTracker(): null {
  const { isLoaded, isSignedIn } = useAuth();
  const { isMobileDevice, hasResolved } = useIsMobile();
  const hasSyncedRef = useRef(false);

  useEffect(() => {
    ensureLandingCookie();
  }, []);

  useEffect(() => {
    if (!isLoaded || !isSignedIn || !hasResolved || hasSyncedRef.current) {
      return;
    }

    if (readSyncedFlag()) {
      hasSyncedRef.current = true;
      return;
    }

    const landing = ensureLandingCookie();

    if (!landing) {
      return;
    }

    hasSyncedRef.current = true;
    writeSyncedFlag();

    recordLandingAttribution({
      visitorId: landing.visitorId,
      referrerUrl: landing.referrerUrl,
      utmSource: landing.utmSource,
      utmMedium: landing.utmMedium,
      utmCampaign: landing.utmCampaign,
      utmContent: landing.utmContent,
      utmTerm: landing.utmTerm,
      landingPath: landing.landingPath,
      sourcePage: landing.sourcePage,
      sourceCta: landing.sourceCta,
      sourceReferrer: landing.sourceReferrer,
      landedAt: landing.landedAt,
      isMobileDevice,
    });
  }, [hasResolved, isLoaded, isMobileDevice, isSignedIn]);

  return null;
}
